import React from "react";
import {
  ArrowRight, ExternalLink, Zap, Globe, Cpu, Rocket,
  Shield, CheckCircle, Activity, Star,
} from "../components/Icons";
import {
  Reveal, Button, SectionHeading, PageGlow, AnimatedCounter,
} from "../components/ui";

/* ─── Data ─── */
const FLOATING_PRODUCTS = [
  {
    icon: "📚",
    name: "KwiK",
    category: "Education · Android",
    installs: "450K+",
    rating: "4.2",
    accent: "from-emerald-500/25 to-teal-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.nm.premium",
  },
  {
    icon: "💬",
    name: "nBubble",
    category: "Social · Android",
    installs: "400K+",
    rating: "4.2",
    accent: "from-emerald-500/25 to-teal-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.nm.stable",
  },
  {
    icon: "🔍",
    name: "App Search",
    category: "Tool · Android",
    installs: "400K+",
    rating: "4.4",
    accent: "from-emerald-500/25 to-teal-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.search",
  },
  {
    icon: "🛒",
    name: "1Shop",
    category: "Shopping · Android",
    installs: "50K+",
    rating: "4.6",
    accent: "from-emerald-500/25 to-teal-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.nm",
  },
  {
    icon: "📙",
    name: "Apz Notes",
    category: "Notes · Android",
    installs: "50K+",
    rating: "4.3",
    accent: "from-accent-500/25 to-blue-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.motivate.me",
  },
  {
    icon: "🔢",
    name: "Digits",
    category: "Utility · Android",
    installs: "50K+",
    rating: "4.4",
    accent: "from-accent-500/25 to-blue-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.digits&hl=en",
  },
  {
    icon: "📝",
    name: "MyLog",
    category: "Log · Android",
    installs: "50K+",
    rating: "4.2",
    accent: "from-accent-500/25 to-blue-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.san",
  },
  {
    icon: "⏱️",
    name: "Poke Me",
    category: "Health · Android",
    installs: "40K+",
    rating: "4.4",
    accent: "from-violet-500/25 to-purple-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.pria",
  },
  {
    icon: "🔒",
    name: "Apz Lock",
    category: "Privacy · Android",
    installs: "30K+",
    rating: "4.2",
    accent: "from-violet-500/25 to-purple-500/10",
    url: "https://play.google.com/store/apps/details?id=pt.nm",
  },
  {
    icon: "🏠",
    name: "AditiRealty",
    category: "Web Platform",
    installs: "Live",
    rating: "2L+ MAU",
    accent: "from-cyan-500/25 to-sky-500/10",
    url: "https://www.aditirealty.com",
  },
  {
    icon: "❄️",
    name: "AirXSolar",
    category: "Web Platform",
    installs: "Live",
    rating: "50K+ MAU",
    accent: "from-cyan-500/25 to-sky-500/10",
    url: "https://www.airxsolar.com.au/",
  },
  {
    icon: "💡",
    name: "Beam",
    category: "Tools · Android",
    installs: "5K",
    rating: "4.2",
    accent: "from-cyan-500/25 to-sky-500/10",
    url: "https://play.google.com/store/apps/details?id=com.ram.chocolate.torch",
  },
];

const STATS = [
  { value: "30+", label: "Global Apps Launched",     icon: Rocket },
  { value: "12+", label: "Enterprise Web Platforms", icon: Globe },
  { value: "5M+", label: "Active Installs",          icon: Activity },
  { value: "15",  label: "Days Avg. Shipping Time",  icon: Zap },
];

const PIPELINE = [
  {
    days: "Days 1–3",
    title: "Blueprint & AI Modeling",
    desc: "Deep architectural scoping, automated schema generation, and AI-driven technical specification. We map every edge case before a single line of code is written.",
    icon: Cpu,
    color: "accent",
    tags: ["System Architecture", "AI Schema Gen", "Tech Spec"],
  },
  {
    days: "Days 4–10",
    title: "High-Velocity Engineering",
    desc: "Parallel development streams — high-concurrency backend logic in Go & Java alongside cross-platform compilation for mobile, desktop, and web surfaces simultaneously.",
    icon: Zap,
    color: "violet",
    tags: ["Go / Java Backend", "React Native", "API Design"],
  },
  {
    days: "Days 11–13",
    title: "Stress Testing & Optimization",
    desc: "Rigorous load testing at 10× expected peak, code optimization, security hardening, and full cloud staging across AWS / Azure / GCP before any user touches the product.",
    icon: Shield,
    color: "emerald",
    tags: ["Load Testing", "Security Audit", "Cloud Staging"],
  },
  {
    days: "Days 14–15",
    title: "Deployment & Day-1 Scale",
    desc: "Zero-downtime production rollout via Kubernetes orchestration with autoscaling pre-configured. We stay on for 24-hour post-launch monitoring with dedicated SLA coverage.",
    icon: Rocket,
    color: "cyan",
    tags: ["K8s Rollout", "Autoscaling", "SLA Coverage"],
  },
];

const TECH_STACK = [
  { name: "Android",           desc: "Native"                  },
  { name: "iOS",               desc: "Native"                  },
  { name: "Go Lang",           desc: "High-perf backends"      },
  { name: "Java",              desc: "Enterprise concurrency"  },
  { name: "Node.js",           desc: "API layer"               },
  { name: "Next.js",           desc: "Web platforms"           },
  { name: "React Native",      desc: "Cross-platform mobile"   },
  { name: "Kubernetes",        desc: "Orchestration"           },
  { name: "AWS / GCP / Azure", desc: "Multi-cloud"             },
  { name: "PostgreSQL",        desc: "Relational data"         },
];

const PIPELINE_COLORS = {
  accent:  { ring: "border-accent-500/25",  bg: "bg-accent-500/10",  text: "text-accent-600",  num: "from-accent-500 to-violet-500"   },
  violet:  { ring: "border-violet-500/25",  bg: "bg-violet-500/10",  text: "text-violet-600",  num: "from-violet-500 to-purple-500"   },
  emerald: { ring: "border-emerald-500/25", bg: "bg-emerald-500/10", text: "text-emerald-600", num: "from-emerald-500 to-teal-500"    },
  cyan:    { ring: "border-cyan-500/25",    bg: "bg-cyan-500/10",    text: "text-cyan-600",    num: "from-cyan-500 to-sky-500"        },
};

/* ─── Sub-components ─── */
function FloatingCard({ product }) {
  return (
    <button
      onClick={() => window.open(product.url, "_blank", "noopener,noreferrer")}
      className="card-interactive group relative p-5 text-left w-full"
      aria-label={"Open " + product.name}
    >
      <div className="flex items-start justify-between mb-3">
        <span className={"w-11 h-11 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br " + product.accent + " border border-zinc-200"}>
          {product.icon}
        </span>
        <ExternalLink size={14} className="text-zinc-400 group-hover:text-accent-600 transition-colors mt-0.5" />
      </div>
      <p className="text-zinc-900 font-semibold text-sm mb-0.5">{product.name}</p>
      <p className="text-zinc-500 text-xs mb-3">{product.category}</p>
      <div className="flex items-center justify-between">
        <span className="text-zinc-600 text-xs">{product.installs} installs</span>
        <span className="flex items-center gap-1 text-amber-500 text-xs font-medium">
          <Star size={11} />
          {product.rating}
        </span>
      </div>
    </button>
  );
}

function PipelineCard({ step, index }) {
  const c = PIPELINE_COLORS[step.color];
  const Icon = step.icon;
  return (
    <Reveal
      delay={index * 80}
      className={"card-interactive relative p-7 flex flex-col gap-4 " + c.ring}
    >
      <div className="flex items-center gap-4">
        <span className={"w-11 h-11 rounded-xl flex items-center justify-center text-white font-black bg-gradient-to-br " + c.num + " shadow-glow"}>
          {index + 1}
        </span>
        <div>
          <p className={"text-xs font-semibold uppercase tracking-wider " + c.text}>{step.days}</p>
          <h3 className="text-zinc-900 font-bold text-base leading-tight">{step.title}</h3>
        </div>
        <span className={"ml-auto " + c.text + " opacity-70"}><Icon size={20} /></span>
      </div>
      <p className="text-zinc-600 text-sm leading-relaxed">{step.desc}</p>
      <div className="flex flex-wrap gap-2 mt-auto pt-1">
        {step.tags.map((tag) => (
          <span key={tag} className={"px-2.5 py-1 rounded-lg text-xs font-medium " + c.text + " " + c.bg + " border " + c.ring}>
            {tag}
          </span>
        ))}
      </div>
    </Reveal>
  );
}

/* ─── Page ─── */
export default function HomePage({ navigate }) {
  return (
    <div>
      {/* ── Hero ── */}
      <section className="relative flex flex-col items-center justify-center px-6 pt-16 pb-20 overflow-hidden">
        <PageGlow />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-200 bg-zinc-100 backdrop-blur-sm text-zinc-600 text-xs font-semibold uppercase tracking-[0.16em] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Premium Tech Studio · Bengaluru, India
            </span>
          </Reveal>

          <Reveal delay={80} as="h1" className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-6">
            <span className="text-gradient-soft">Concept to</span>
            <br />
            <span className="text-gradient-brand">Production in 15&nbsp;Days.</span>
          </Reveal>

          <Reveal delay={160} as="p" className="text-zinc-600 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed mb-10">
            We build, deploy, scale, and maintain high-performance digital ecosystems. Native
            Android &amp; iOS apps, macOS applications, web platforms, and complex enterprise
            software — powered by AI automation and elite engineers.
          </Reveal>

          <Reveal delay={240} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate("contact")}>
              <Zap size={18} />
              Launch Project
              <ArrowRight size={18} />
            </Button>
            <Button size="lg" variant="secondary" onClick={() => navigate("ecosystem")}>
              Explore Ecosystem
              <ArrowRight size={18} />
            </Button>
          </Reveal>
        </div>

        {/* Floating product banners */}
        <div className="relative z-10 w-full max-w-5xl mx-auto mt-16">
          <Reveal as="p" className="text-center text-zinc-500 text-xs font-semibold uppercase tracking-[0.18em] mb-6">
            Live products — click to explore
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {FLOATING_PRODUCTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 50}>
                <FloatingCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="border-y border-zinc-200 bg-zinc-50 py-14" aria-label="Key metrics">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            {STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.label} delay={i * 70} className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-accent-500/10 border border-accent-500/20 flex items-center justify-center text-accent-600 flex-shrink-0">
                    <Icon size={22} />
                  </span>
                  <div>
                    <p className="text-3xl font-black text-zinc-900 leading-none">
                      <AnimatedCounter value={s.value} />
                    </p>
                    <p className="text-zinc-500 text-xs mt-1 leading-snug">{s.label}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 15-Day Pipeline ── */}
      <section className="py-20 px-6" aria-labelledby="pipeline-heading">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-12">
            <SectionHeading
              eyebrow="The Core Blueprint"
              title="The 15-Day Deployment Pipeline"
              subtitle="Enterprise-grade delivery, hyper-accelerated. Every stage is precision-engineered to eliminate delay without sacrificing architectural integrity or code quality."
              id="pipeline-heading"
            />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PIPELINE.map((step, i) => (
              <PipelineCard key={step.title} step={step} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section className="py-16 border-t border-zinc-200 bg-zinc-50 overflow-hidden" aria-label="Technology stack">
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <p className="text-zinc-500 text-xs font-semibold uppercase tracking-[0.18em]">
            Engineered on the world's most powerful stack
          </p>
        </div>
        <div className="flex gap-4 px-6 flex-wrap justify-center">
          {TECH_STACK.map((t, i) => (
            <Reveal
              key={t.name}
              delay={i * 40}
              className="flex items-center gap-3 px-5 py-3 rounded-full border border-zinc-200 bg-white hover:bg-zinc-100 hover:border-zinc-300 transition-colors whitespace-nowrap"
            >
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-accent-400 to-emerald-400 flex-shrink-0" />
              <span className="text-zinc-900 text-sm font-semibold">{t.name}</span>
              <span className="text-zinc-500 text-xs">{t.desc}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 px-6" aria-labelledby="cta-heading">
        <div className="max-w-4xl mx-auto">
          <Reveal className="relative rounded-3xl border border-zinc-200 bg-white shadow-card px-6 py-10 sm:p-12 overflow-hidden text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[440px] h-[260px] rounded-full bg-accent-500/[0.07] blur-[90px] pointer-events-none" />
            <div className="relative">
              <span className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 items-center justify-center text-emerald-600 mb-5">
                <CheckCircle size={28} />
              </span>
              <h2 id="cta-heading" className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 mb-3">
                Your next product ships in 15 days.
              </h2>
              <p className="text-zinc-600 text-base max-w-xl mx-auto mb-7">
                Tell us your vision. We'll architect, build, and deploy a production-grade
                system while your competitors are still scoping requirements.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button size="lg" onClick={() => navigate("contact")}>
                  Start the Conversation
                  <ArrowRight size={18} />
                </Button>
                <button
                  onClick={() => navigate("about")}
                  className="text-zinc-600 hover:text-zinc-900 text-sm font-medium transition-colors"
                >
                  How it works →
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
